import React, { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import "./Dashboard.css";
import {
  fetchClientsForScope,
  fetchAgent247Events,
  fetchAgentAlbertAlerts,
  fetchAipChangesForScope,
  AGENT_ALERTS_FETCH_LIMIT,
  shouldUseUnityCompanyClientScope,
} from "../utils/api";
import {
  countUnreadAiAssistantEvents,
  getSeenEventIds,
  getStableEventId,
  markAiAssistantEventSeen,
} from "../utils/aiAssistantReadState";
import {
  countUnreadAgentAlbertAlerts,
  getSeenAgentAlbertAlertIds,
  getStableAgentAlbertAlertId,
  markAgentAlbertAlertSeen,
} from "../utils/agentAlbertAlertReadState";
import { resolveCallerFirstName } from "../utils/agent247CallerDisplay";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUsers,
  faChartLine,
  faCheckCircle,
  faQuestionCircle,
  faExclamationTriangle,
  faExclamationCircle,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";

import ClientAvatar from "./ClientAvatar";
import AipChangeCenterTable from "./AipChangeCenterTable";
import "./AipChangeCenterTable.css";
import { AIP_CHANGE_RANGE_OPTIONS } from "../utils/aipChangeCenterUi";
import {
  countWellSkyClientStatuses,
  getClientStatusFilterKey,
  isMonitoredCareHubClient,
} from "../utils/clientWellSkyStatus";

const DEV_AI_ASSISTANT_ORG_ID = "695efe91312e4c3878fcf24a";
const isDevAiAssistantEnv = process.env.NODE_ENV !== "production";
const DASHBOARD_CHANGE_ROW_LIMIT = 5;

const Dashboard = ({
  activeLocation,
  locations = [],
  onChangeLocation,
  companyDisplayName,
  currentUser,
  onLogout,
  onLock,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allClients, setAllClients] = useState([]);
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalClients, setModalClients] = useState([]);

  // Disclaimer toggle state
  const [showDisclaimer, setShowDisclaimer] = useState(false);

  // AIP Change Center state
  const [changeRange, setChangeRange] = useState("today");
  const [changeData, setChangeData] = useState(null);   // { range, results, totalMatching }
  const [changeLoading, setChangeLoading] = useState(false);
  const [changeError, setChangeError] = useState(null);

  const [aiAssistantEvents, setAiAssistantEvents] = useState([]);
  const [unreadAiAssistantCount, setUnreadAiAssistantCount] = useState(0);
  const [aiCallAssistMenuOpen, setAiCallAssistMenuOpen] = useState(false);
  const [agentAlbertAlerts, setAgentAlbertAlerts] = useState([]);
  const [agentAlbertAlertsError, setAgentAlbertAlertsError] = useState("");
  const [unreadAgentAlbertCount, setUnreadAgentAlbertCount] = useState(0);
  const [agentAlbertAlertMenuOpen, setAgentAlbertAlertMenuOpen] = useState(false);
  const aiAssistantEventsRef = useRef([]);
  const aiAssistantOrgRef = useRef("");
  const agentAlbertAlertsRef = useRef([]);
  const aiCallAssistWrapRef = useRef(null);
  const agentAlbertAlertWrapRef = useRef(null);

  const activeLocationId = useMemo(
    () => String(activeLocation?._id || activeLocation?.id || "").trim(),
    [activeLocation],
  );

  const wellSkyStatusCounts = useMemo(
    () => countWellSkyClientStatuses(allClients),
    [allClients],
  );
  const hasWellSkyStatusCounts = wellSkyStatusCounts.total > 0;

  /** WellSky locations: KPIs and modals use active roster only (exclude DeActive). */
  const dashboardClients = useMemo(() => {
    if (!hasWellSkyStatusCounts) return allClients;
    return allClients.filter((c) => isMonitoredCareHubClient(c));
  }, [allClients, hasWellSkyStatusCounts]);

  const stats = useMemo(() => {
    const clients = dashboardClients;
    const totalClients = clients.length;
    const scores = clients.map((c) => Number(c.aipScore) || 0);
    const scored = scores.filter((s) => s > 0);
    const avgAip =
      scored.length > 0
        ? Math.round(scored.reduce((a, b) => a + b, 0) / scored.length)
        : 0;
    const aip75 = scores.filter((s) => s >= 75).length;
    const aip40_75 = scores.filter((s) => s > 0 && s < 75 && s >= 40).length;
    const aip_lt40 = scores.filter((s) => s > 0 && s < 40).length;
    const aip_need = scores.filter((s) => s <= 0).length;
    return {
      totalClients,
      avgAip,
      aip75,
      aip40_75,
      aip_lt40,
      aip_need,
    };
  }, [dashboardClients]);

  const aiAssistantOrganizationId = useMemo(() => {
    const locationId = activeLocation?._id || activeLocation?.id;
    const userLocation =
      currentUser?.locationId ||
      currentUser?.activeLocationId ||
      (Array.isArray(currentUser?.locations) ? currentUser.locations[0] : "");
    const locationCompany =
      activeLocation?.companyId?._id ||
      activeLocation?.companyId?.id ||
      activeLocation?.companyId;
    const userCompany =
      currentUser?.companyId ||
      currentUser?.company?._id ||
      currentUser?.company?.id;
    const resolved = String(
      locationId || userLocation || locationCompany || userCompany || "",
    ).trim();
    if (resolved) return resolved;
    if (isDevAiAssistantEnv) return DEV_AI_ASSISTANT_ORG_ID;
    return "";
  }, [activeLocation, currentUser]);

  // AIP Change Center — refetch when location or range changes
  useEffect(() => {
    if (!activeLocationId) {
      setChangeData(null);
      return;
    }
    let active = true;
    setChangeLoading(true);
    setChangeError(null);
    fetchAipChangesForScope(
      activeLocation,
      locations,
      changeRange,
      DASHBOARD_CHANGE_ROW_LIMIT,
    )
      .then((payload) => {
        if (!active) return;
        const merged = payload?.results || [];
        const results = merged.slice(0, DASHBOARD_CHANGE_ROW_LIMIT);
        setChangeData({
          ...payload,
          results,
          unityMerged: shouldUseUnityCompanyClientScope(activeLocation),
        });
      })
      .catch((err) => {
        if (!active) return;
        setChangeError(err?.response?.data?.message || "Failed to load score changes");
        setChangeData(null);
      })
      .finally(() => {
        if (active) setChangeLoading(false);
      });
    return () => { active = false; };
  }, [activeLocationId, changeRange, activeLocation, locations]);

  useEffect(() => {
    aiAssistantEventsRef.current = aiAssistantEvents;
  }, [aiAssistantEvents]);

  useEffect(() => {
    agentAlbertAlertsRef.current = agentAlbertAlerts;
  }, [agentAlbertAlerts]);

  useEffect(() => {
    aiAssistantOrgRef.current = aiAssistantOrganizationId;
  }, [aiAssistantOrganizationId]);

  useEffect(() => {
    setUnreadAiAssistantCount(
      countUnreadAiAssistantEvents(aiAssistantEvents, aiAssistantOrganizationId),
    );
  }, [aiAssistantEvents, aiAssistantOrganizationId]);

  useEffect(() => {
    const onSeenUpdated = (ev) => {
      const oid = String(ev.detail?.organizationId || "");
      if (oid !== aiAssistantOrgRef.current) return;
      setUnreadAiAssistantCount(
        countUnreadAiAssistantEvents(aiAssistantEventsRef.current, oid),
      );
    };
    window.addEventListener("aiAssistantSeenUpdated", onSeenUpdated);
    return () => window.removeEventListener("aiAssistantSeenUpdated", onSeenUpdated);
  }, []);

  useEffect(() => {
    const onStorage = (e) => {
      const prefix = "aiAssistantSeenEvents:";
      if (!e.key || !e.key.startsWith(prefix)) return;
      const oid = e.key.slice(prefix.length);
      if (oid !== aiAssistantOrgRef.current) return;
      setUnreadAiAssistantCount(
        countUnreadAiAssistantEvents(aiAssistantEventsRef.current, oid),
      );
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    if (!aiAssistantOrganizationId) {
      setAiAssistantEvents([]);
      return undefined;
    }
    let active = true;
    const poll = async () => {
      try {
        const rows = await fetchAgent247Events(aiAssistantOrganizationId, 150);
        if (!active) return;
        const list = Array.isArray(rows) ? rows : [];
        setAiAssistantEvents(list);
      } catch {
        if (active) setAiAssistantEvents([]);
      }
    };
    void poll();
    const interval = setInterval(poll, 25000);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [aiAssistantOrganizationId]);

  useEffect(() => {
    setUnreadAgentAlbertCount(
      countUnreadAgentAlbertAlerts(agentAlbertAlerts, activeLocationId),
    );
  }, [agentAlbertAlerts, activeLocationId]);

  useEffect(() => {
    const onSeenUpdated = (ev) => {
      const lid = String(ev.detail?.locationId || "");
      if (lid !== activeLocationId) return;
      setUnreadAgentAlbertCount(
        countUnreadAgentAlbertAlerts(agentAlbertAlertsRef.current, lid),
      );
    };
    window.addEventListener("agentAlbertAlertSeenUpdated", onSeenUpdated);
    return () => window.removeEventListener("agentAlbertAlertSeenUpdated", onSeenUpdated);
  }, [activeLocationId]);

  useEffect(() => {
    const onStorage = (e) => {
      const prefix = "agentAlbertAlertSeen:";
      if (!e.key || !e.key.startsWith(prefix)) return;
      const lid = e.key.slice(prefix.length);
      if (lid !== activeLocationId) return;
      setUnreadAgentAlbertCount(
        countUnreadAgentAlbertAlerts(agentAlbertAlertsRef.current, lid),
      );
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [activeLocationId]);

  useEffect(() => {
    if (!activeLocationId) {
      setAgentAlbertAlerts([]);
      setAgentAlbertAlertsError("");
      return undefined;
    }
    let active = true;
    const poll = async () => {
      try {
        const payload = await fetchAgentAlbertAlerts(activeLocationId, AGENT_ALERTS_FETCH_LIMIT);
        if (!active) return;
        const list = Array.isArray(payload?.alerts) ? payload.alerts : [];
        setAgentAlbertAlerts(list);
        setAgentAlbertAlertsError("");
      } catch (err) {
        if (!active) return;
        setAgentAlbertAlerts([]);
          setAgentAlbertAlertsError(
            err?.response?.data?.message ||
              err?.message ||
              "Failed to load shift reports.",
          );
      }
    };
    void poll();
    const interval = setInterval(poll, 60000);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [activeLocationId]);

  useEffect(() => {
    if (!aiCallAssistMenuOpen && !agentAlbertAlertMenuOpen) return undefined;
    const onDocMouseDown = (e) => {
      const inCallAssist =
        aiCallAssistWrapRef.current && aiCallAssistWrapRef.current.contains(e.target);
      const inAgentAlert =
        agentAlbertAlertWrapRef.current &&
        agentAlbertAlertWrapRef.current.contains(e.target);
      if (!inCallAssist) setAiCallAssistMenuOpen(false);
      if (!inAgentAlert) setAgentAlbertAlertMenuOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setAiCallAssistMenuOpen(false);
        setAgentAlbertAlertMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [aiCallAssistMenuOpen, agentAlbertAlertMenuOpen]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);

      // If no location selected, show empty data
      const locId = activeLocation?.id || activeLocation?._id;
      const activeCompanyId =
        activeLocation?.companyId?._id ||
        activeLocation?.companyId?.id ||
        activeLocation?.companyId;
      if (!locId) {
        setAllClients([]);
        setLoading(false);
        return;
      }

      try {
        const clients = await fetchClientsForScope(activeLocation, locations);
        setAllClients(clients);
      } catch (err) {
        console.error("Dashboard load failed", err);
        setError("Failed to load dashboard data");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [activeLocation, locations]);

  // Handle card click to show filtered clients
  const handleCardClick = (category) => {
    let filtered = [];
    let title = "";

    const scope = dashboardClients;

    switch (category) {
      case "aip75":
        filtered = scope.filter((c) => Number(c.aipScore) >= 75);
        title = "Clients with AIP Score 75+";
        break;
      case "aip40_75":
        filtered = scope.filter((c) => {
          const score = Number(c.aipScore);
          return score >= 40 && score < 75;
        });
        title = "Clients with AIP Score 40-75";
        break;
      case "aip_lt40":
        filtered = scope.filter((c) => {
          const score = Number(c.aipScore);
          return score > 0 && score < 40;
        });
        title = "Clients with AIP Score <40";
        break;
      case "aip_need":
        filtered = scope.filter(
          (c) => !c.aipScore || Number(c.aipScore) <= 0,
        );
        title = "Clients Needing AIP Data";
        break;
      case "active":
        filtered = scope.filter(
          (c) => getClientStatusFilterKey(c) === "active",
        );
        title = "Active Clients";
        break;
      case "deactive":
        filtered = allClients.filter(
          (c) => getClientStatusFilterKey(c) === "inactive",
        );
        title = "DeActive Clients";
        break;
      default:
        return;
    }

    setModalTitle(title);
    setModalClients(filtered);
    setShowModal(true);
  };

  // Navigate to client AIP analysis page
  const handleClientClick = (clientId) => {
    setShowModal(false);
    navigate(`/client-management/client/${clientId}/aip-analysis`);
  };

  const getScoreColor = (score) => {
    if (!score || score <= 0) return "#8c8c8c";
    if (score >= 75) return "#52c41a";
    if (score >= 40) return "#faad14";
    return "#f5222d";
  };
  const isAutoProvisional = (client) =>
    client?.isAipProvisional === true ||
    String(client?.latestAssessmentStatus || "").toLowerCase() ===
      "auto-provisional";

  const getClientDisplayName = (client) => {
    const first = String(client?.firstName || "").trim();
    const last = String(client?.lastName || "").trim();
    const full = `${first} ${last}`.trim();
    return (
      full ||
      String(client?.name || client?.fullName || "Unknown Client").trim() ||
      "Unknown Client"
    );
  };

  const pct = (val) =>
    stats.totalClients > 0
      ? `${Math.round((val / stats.totalClients) * 100)}% of clients`
      : "0% of clients";

  const formatCallAssistTime = (value) => {
    if (!value) return "--";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "--";
    return date.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  // AI 24/7 Activity Center — event helpers
  const isAiCallAssistEventUnread = (ev) => {
    const id = getStableEventId(ev);
    if (!id || !aiAssistantOrganizationId) return false;
    return !getSeenEventIds(aiAssistantOrganizationId).has(id);
  };

  const aiCallAssistListEvents = useMemo(() => {
    return [...(Array.isArray(aiAssistantEvents) ? aiAssistantEvents : [])]
      .sort(
        (a, b) =>
          new Date(b?.timestamp || b?.createdAt || 0).getTime() -
          new Date(a?.timestamp || a?.createdAt || 0).getTime(),
      )
      .slice(0, 25);
  }, [aiAssistantEvents]);

  const handleAiCallAssistRowActivate = (ev) => {
    if (!aiAssistantOrganizationId) return;
    markAiAssistantEventSeen(aiAssistantOrganizationId, ev);
    setUnreadAiAssistantCount(
      countUnreadAiAssistantEvents(aiAssistantEventsRef.current, aiAssistantOrganizationId),
    );
    setAiCallAssistMenuOpen(false);
    const id = getStableEventId(ev);
    if (id) navigate(`/agent247?event=${encodeURIComponent(id)}`);
    else navigate("/agent247");
  };

  const isAgentAlbertAlertUnread = (alert) => {
    const id = getStableAgentAlbertAlertId(alert);
    if (!id || !activeLocationId) return false;
    return !getSeenAgentAlbertAlertIds(activeLocationId).has(id);
  };

  const agentAlbertAlertsList = useMemo(() => {
    return [...(Array.isArray(agentAlbertAlerts) ? agentAlbertAlerts : [])]
      .sort(
        (a, b) =>
          new Date(b?.occurredAt || 0).getTime() -
          new Date(a?.occurredAt || 0).getTime(),
      )
      .slice(0, 25);
  }, [agentAlbertAlerts]);

  const handleAgentAlbertAlertRowActivate = (alert) => {
    if (!activeLocationId) return;
    markAgentAlbertAlertSeen(activeLocationId, alert);
    setUnreadAgentAlbertCount(
      countUnreadAgentAlbertAlerts(agentAlbertAlertsRef.current, activeLocationId),
    );
    setAgentAlbertAlertMenuOpen(false);
    const id = getStableAgentAlbertAlertId(alert);
    if (id) navigate(`/albertai-alerts?alert=${encodeURIComponent(id)}`);
    else navigate("/albertai-alerts");
  };

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Good Morning";
    if (h < 17) return "Good Afternoon";
    return "Good Evening";
  })();
  const firstName = currentUser?.name?.split(" ")[0] || "there";

  // AI 24/7 Activity Center — wired metrics
  const inquiryCallsCount = useMemo(
    () =>
      new Set(
        (Array.isArray(aiAssistantEvents) ? aiAssistantEvents : [])
          .map((e) => String(e?.callId || "").trim())
          .filter(Boolean),
      ).size,
    [aiAssistantEvents],
  );
  const aiResolutionsCount = useMemo(
    () =>
      (Array.isArray(aiAssistantEvents) ? aiAssistantEvents : []).filter(
        (e) => String(e?.followUpStatus || "").toLowerCase() === "resolved",
      ).length,
    [aiAssistantEvents],
  );

  // AI Shift Report metrics — kept for potential re-use; widget not rendered on Dashboard
  const shiftAlertsToday = useMemo(() => {
    const startOfDay = new Date();
    startOfDay.setHours(0, 0, 0, 0);
    return (Array.isArray(agentAlbertAlerts) ? agentAlbertAlerts : []).filter(
      (a) => new Date(a?.occurredAt || 0) >= startOfDay,
    ).length;
  }, [agentAlbertAlerts]);

  const shiftAlertsCritical = useMemo(
    () =>
      (Array.isArray(agentAlbertAlerts) ? agentAlbertAlerts : []).filter(
        (a) => a?.severity === "critical" || a?.severity === "high",
      ).length,
    [agentAlbertAlerts],
  );

  const shiftAlertsOpen = useMemo(
    () =>
      (Array.isArray(agentAlbertAlerts) ? agentAlbertAlerts : []).filter(
        (a) => a?.followUpStatus === "open",
      ).length,
    [agentAlbertAlerts],
  );

  const shiftAlertsResolved = useMemo(
    () =>
      (Array.isArray(agentAlbertAlerts) ? agentAlbertAlerts : []).filter(
        (a) => a?.followUpStatus === "resolved",
      ).length,
    [agentAlbertAlerts],
  );

  return (
    <div className="dashboard-container page-shell">

      {/* ── Greeting row ── */}
      <div className="db-greeting-row">
        <div>
          <h1 className="db-greeting">
            {greeting}, {firstName} 👋
          </h1>
          <p className="db-greeting-sub">
            {activeLocation?.name
              ? `Here's what's happening at ${activeLocation.name} today.`
              : "Here's what's happening with your agency today."}
          </p>
        </div>
        <div className="db-last-updated">
          {!loading && (
            <span>
              Updated{" "}
              {new Date().toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
          )}
        </div>
      </div>

      {/* ── Status banners ── */}
      {loading && (
        <div className="dashboard-status">Loading dashboard data…</div>
      )}
      {error && <div className="dashboard-status error">{error}</div>}

      {/* ── KPI cards — 6 in 2 rows of 3 ── */}
      <div className="db-kpi-grid">
        <div
          className={`db-kpi-card db-kpi-blue${
            hasWellSkyStatusCounts ? " clickable" : ""
          }`}
          title={
            hasWellSkyStatusCounts
              ? "WellSky active clients — click to view list"
              : "Total clients in this location"
          }
          onClick={
            hasWellSkyStatusCounts
              ? () => handleCardClick("active")
              : undefined
          }
          role={hasWellSkyStatusCounts ? "button" : undefined}
          tabIndex={hasWellSkyStatusCounts ? 0 : undefined}
          onKeyDown={
            hasWellSkyStatusCounts
              ? (e) =>
                  (e.key === "Enter" || e.key === " ") &&
                  handleCardClick("active")
              : undefined
          }
        >
          <div className="db-kpi-icon-wrap db-kpi-icon-blue">
            <FontAwesomeIcon icon={faUsers} />
          </div>
          <div className="db-kpi-body">
            <div className="db-kpi-value">
              {hasWellSkyStatusCounts
                ? wellSkyStatusCounts.active
                : stats.totalClients}
            </div>
            <div className="db-kpi-label">
              {hasWellSkyStatusCounts ? "Active Clients" : "Total Clients"}
            </div>
          </div>
        </div>

        <div
          className="db-kpi-card db-kpi-green"
          title={
            hasWellSkyStatusCounts
              ? "Average AIP score across active assessed clients"
              : "Average AIP score across all assessed clients"
          }
        >
          <div className="db-kpi-icon-wrap db-kpi-icon-green">
            <FontAwesomeIcon icon={faChartLine} />
          </div>
          <div className="db-kpi-body">
            <div className="db-kpi-value">{stats.avgAip || "—"}</div>
            <div className="db-kpi-label">Average AIP Score</div>
          </div>
        </div>

        <div
          className="db-kpi-card db-kpi-gray clickable"
          onClick={() => handleCardClick("aip_need")}
          title={
            hasWellSkyStatusCounts
              ? "Active clients with no AIP score yet — click to view"
              : "Clients with no AIP score yet — click to view"
          }
          role="button"
          tabIndex={0}
          onKeyDown={(e) =>
            (e.key === "Enter" || e.key === " ") && handleCardClick("aip_need")
          }
        >
          <div className="db-kpi-icon-wrap db-kpi-icon-gray">
            <FontAwesomeIcon icon={faQuestionCircle} />
          </div>
          <div className="db-kpi-body">
            <div className="db-kpi-value">{stats.aip_need}</div>
            <div className="db-kpi-label">Clients Need Data</div>
          </div>
        </div>

        <div
          className="db-kpi-card db-kpi-green clickable"
          onClick={() => handleCardClick("aip75")}
          title="Clients with AIP score 75+ — click to view"
          role="button"
          tabIndex={0}
          onKeyDown={(e) =>
            (e.key === "Enter" || e.key === " ") && handleCardClick("aip75")
          }
        >
          <div className="db-kpi-icon-wrap db-kpi-icon-green">
            <FontAwesomeIcon icon={faCheckCircle} />
          </div>
          <div className="db-kpi-body">
            <div className="db-kpi-value">{stats.aip75}</div>
            <div className="db-kpi-label">AIP Score 75+</div>
            <div className="db-kpi-sub">{pct(stats.aip75)}</div>
          </div>
        </div>

        <div
          className="db-kpi-card db-kpi-orange clickable"
          onClick={() => handleCardClick("aip40_75")}
          title="Clients with AIP score 40–75 — click to view"
          role="button"
          tabIndex={0}
          onKeyDown={(e) =>
            (e.key === "Enter" || e.key === " ") && handleCardClick("aip40_75")
          }
        >
          <div className="db-kpi-icon-wrap db-kpi-icon-orange">
            <FontAwesomeIcon icon={faExclamationTriangle} />
          </div>
          <div className="db-kpi-body">
            <div className="db-kpi-value">{stats.aip40_75}</div>
            <div className="db-kpi-label">AIP Score 40–75</div>
            <div className="db-kpi-sub">{pct(stats.aip40_75)}</div>
          </div>
        </div>

        <div
          className="db-kpi-card db-kpi-red clickable"
          onClick={() => handleCardClick("aip_lt40")}
          title="Clients with AIP score below 40 — click to view"
          role="button"
          tabIndex={0}
          onKeyDown={(e) =>
            (e.key === "Enter" || e.key === " ") && handleCardClick("aip_lt40")
          }
        >
          <div className="db-kpi-icon-wrap db-kpi-icon-red">
            <FontAwesomeIcon icon={faExclamationCircle} />
          </div>
          <div className="db-kpi-body">
            <div className="db-kpi-value">{stats.aip_lt40}</div>
            <div className="db-kpi-label">AIP Score &lt;40</div>
            <div className="db-kpi-sub">{pct(stats.aip_lt40)}</div>
          </div>
        </div>
      </div>

      {/* ── AIP Change Center — full width ── */}
      <div className="db-section db-change-center-full">
        <div className="db-section-header">
          <h2 className="db-section-title">AIP Change Center</h2>
          <div className="db-change-header-right">
            <select
              className="db-range-select"
              value={changeRange}
              onChange={(e) => setChangeRange(e.target.value)}
              aria-label="Score change date range"
            >
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
            </select>
            <Link
              to={`/aip-change-center?range=${encodeURIComponent(changeRange)}`}
              className="db-section-viewall"
            >
              View All →
            </Link>
          </div>
        </div>

        <div className="db-section-body db-change-center-body">
        {changeLoading && (
          <div className="db-change-loading">Loading score changes…</div>
        )}

        {!changeLoading && changeError && (
          <div className="db-change-empty db-change-error">{changeError}</div>
        )}

        {!changeLoading && !changeError && changeData && changeData.results.length === 0 && (
          <div className="db-change-empty">
            {changeData.noHistory
              ? "No AIP score history recorded yet for this location."
              : changeData.noClients
              ? "No clients found for this location."
              : "No AIP score data available for this location yet."}
          </div>
        )}

        {!changeLoading && !changeError && changeData && changeData.results.length > 0 && (
          <>
            {changeData.fallback &&
              changeData.results.every(
                (row) => row.change == null || row.change === 0,
              ) && (
                <p className="db-change-fallback-label">
                  No score changes in this range — showing last updated
                </p>
              )}
            {changeData.fallback &&
              changeData.results.some(
                (row) => row.change != null && row.change !== 0,
              ) && (
                <p className="db-change-fallback-label">
                  Showing most recently updated scores
                </p>
              )}
            <div className="db-change-table-wrap">
              <AipChangeCenterTable
                rows={changeData.results.slice(0, DASHBOARD_CHANGE_ROW_LIMIT)}
                onClientClick={handleClientClick}
              />
            </div>
          </>
        )}
        </div>
      </div>

      {/* ── AI 24/7 Activity Center ── */}
      <div className="db-section">
        <div className="db-section-header">
          <h2 className="db-section-title">AI 24/7 Activity Center</h2>
          <Link to="/agent247" className="db-section-viewall">
            View All
          </Link>
        </div>
        <div className="db-activity-grid">
          <div className="db-activity-card">
            <div className="db-activity-value">{inquiryCallsCount}</div>
            <div className="db-activity-label">Inquiry Calls</div>
            <div className="db-activity-source">from recent events</div>
          </div>
          <div className="db-activity-card db-activity-stub">
            {/* TODO: Active Conversations — no real-time session API exists yet */}
            <div className="db-activity-value">—</div>
            <div className="db-activity-label">Active Conversations</div>
            <div className="db-activity-source">not yet available</div>
          </div>
          <div className="db-activity-card db-activity-stub">
            {/* TODO: Unanswered — no unanswered-call tracking endpoint exists yet */}
            <div className="db-activity-value">—</div>
            <div className="db-activity-label">Unanswered</div>
            <div className="db-activity-source">not yet available</div>
          </div>
          <div className="db-activity-card">
            <div className="db-activity-value">{aiResolutionsCount}</div>
            <div className="db-activity-label">AI Resolutions</div>
            <div className="db-activity-source">resolved follow-ups</div>
          </div>
        </div>

        {/* Recent inquiry calls list */}
        <div className="db-activity-events" ref={aiCallAssistWrapRef}>
          <div className="db-activity-events-header">
            <span>Recent 24/7 Inquiry Calls</span>
            {unreadAiAssistantCount > 0 && (
              <span className="db-unread-badge">{unreadAiAssistantCount} new</span>
            )}
          </div>
          {aiCallAssistListEvents.length === 0 ? (
            <div className="db-activity-empty">No recent calls for this location.</div>
          ) : (
            <div className="db-activity-event-list">
              {aiCallAssistListEvents.slice(0, 5).map((ev) => {
                const unread = isAiCallAssistEventUnread(ev);
                return (
                  <button
                    key={getStableEventId(ev)}
                    type="button"
                    className={`db-event-row${unread ? " is-unread" : ""}`}
                    onClick={() => handleAiCallAssistRowActivate(ev)}
                  >
                    {unread && <span className="db-event-dot" />}
                    <span className="db-event-time">
                      {formatCallAssistTime(ev?.timestamp || ev?.createdAt)}
                    </span>
                    <span className="db-event-caller">
                      {resolveCallerFirstName(ev) || "Unknown caller"}
                    </span>
                    <span className="db-event-status">
                      {ev?.followUpStatus || "open"}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
          <div className="db-activity-events-footer">
            <Link to="/agent247" className="db-viewall-link">
              View all calls →
            </Link>
          </div>
        </div>
      </div>

      {/* ── Legal Disclaimer ── */}
      <div className="dashboard-disclaimer-wrapper">
        <button
          className="dashboard-disclaimer-toggle"
          onClick={() => setShowDisclaimer(!showDisclaimer)}
        >
          {showDisclaimer ? "Hide Legal Disclaimer ▲" : "Read Legal Disclaimer ▼"}
        </button>
        {showDisclaimer && (
          <div className="dashboard-disclaimer">
            You are logged into ALBERTai owned and operated by Unity Global
            Care Inc. and acknowledge and agree to the following: ALBERTai is
            an AI-driven decision support platform providing predictive
            modeling, analytics, and guidance to assist home care executives,
            clinicians, and staff. It is not a substitute for medical advice,
            diagnosis, or treatment from a licensed medical professional or
            from your care team, including home care and home health
            caregivers. Any medical decisions, care plans, diagnoses, or
            treatment recommendations should be based on the professional
            judgment of qualified healthcare providers who have reviewed your
            individual circumstances. By using ALBERTai, you confirm that you
            have read, understood, and agree to Unity Global Care Inc.'s Terms
            of Use and Privacy Policy, and you acknowledge and accept any and
            all responsibilities and liabilities for any unintended or
            misuse-related outcomes arising from the use of ALBERTai. You also
            agree to indemnify, defend, and hold harmless Unity Global Care
            Inc., its employees, investors, directors, partners, and
            contractors from and against any and all claims, losses, damages,
            liabilities, costs, or expenses (including reasonable attorneys'
            fees) arising from or related to your use of ALBERTai. Use of
            ALBERTai is at your own risk; Unity Global Care Inc. does not
            guarantee the accuracy, completeness, or applicability of any
            content generated or utilized by ALBERTai, and it disclaims any
            liability for decisions or actions taken based on its outputs. If
            you have questions about how ALBERTai should be used in your
            specific setting or if you require clinical interpretation, please
            consult your supervising clinician or appropriate medical
            professionals. By continuing to log in, you attest that you have
            read, understood, and agree to this disclaimer, the Terms of Use,
            and the Privacy Policy, and you consent to the handling of
            information as described therein. .
          </div>
        )}
      </div>

      {/* ─────────────────────────────────────────────────────────────
          Everything below is UNCHANGED from the original Dashboard
      ───────────────────────────────────────────────────────────── */}

      {/* Legacy header dropdowns preserved as hidden refs so click-outside
          handlers still bind correctly — dropdowns are now surfaced in the
          sections above instead.  We keep the refs (aiCallAssistWrapRef,
          agentAlbertAlertWrapRef) alive by attaching them in the sections above.
          The following old dropdown JSX is kept but not rendered:            */}
      {false && (
        <div className="dashboard-banner-alerts">
        <div className="dashboard-ai-call-assist-wrap" ref={agentAlbertAlertWrapRef}>
          <button
            type="button"
            className="dashboard-ai-call-assist"
            title="Urgent items from shift report summaries"
            aria-expanded={agentAlbertAlertMenuOpen}
            aria-haspopup="true"
            onClick={() => {
              setAgentAlbertAlertMenuOpen((o) => !o);
              setAiCallAssistMenuOpen(false);
            }}
          >
            <span className="dashboard-ai-call-assist-label">Shift Report</span>
            {unreadAgentAlbertCount > 0 ? (
              <span className="dashboard-ai-call-assist-badge">
                {unreadAgentAlbertCount}
              </span>
            ) : null}
          </button>
        </div>
        </div>
      )}

      {/* AIP Clients Modal */}
      {showModal && (
        <div className="aip-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="aip-modal" onClick={(e) => e.stopPropagation()}>
            <div className="aip-modal-header">
              <h3>{modalTitle}</h3>
              <button
                className="aip-modal-close"
                onClick={() => setShowModal(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="aip-modal-body">
              <div className="aip-client-list">
                {modalClients.length === 0 ? (
                  <div className="aip-empty">No clients in this category</div>
                ) : (
                  modalClients.map((client) => (
                    <div
                      key={client._id || client.id}
                      className="aip-client-row"
                      onClick={() => handleClientClick(client._id || client.id)}
                    >
                      <ClientAvatar
                        client={client}
                        className="aip-client-avatar-small"
                        initialClassName="aip-client-avatar-initial"
                        size={40}
                        alt={getClientDisplayName(client)}
                      />
                      <div className="aip-client-name">
                        <strong>
                          {getClientDisplayName(client)}
                        </strong>
                        <span>
                          ID: {client.clientNumber || client._id?.slice(-6)}
                        </span>
                      </div>
                      <div
                        className="aip-score-pill"
                        style={{
                          backgroundColor: getScoreColor(client.aipScore),
                        }}
                      >
                        {client.aipScore || "N/A"}
                      </div>
                      {isAutoProvisional(client) ? (
                        <span className="aip-provisional-inline">Provisional</span>
                      ) : null}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;

